import { Injectable } from '@angular/core';
import { Item } from '@app/core/components/listbox/listbox.component';
import { DataTaskService } from '@app/core/services/data-task.service';

@Injectable({
    providedIn: 'root',
})
export class AccountingService {
    constructor(private datatask: DataTaskService) {}

    /**
     * Retrieves list of accounting-report layouts.
     *
     * @param res which layouts to retrieve, `'1'` retrieves result layouts, `'0'` retrieves non-result layouts and `'a'` retrieves all layouts.
     */
    public getReportLayouts(res: 'a' | '1' | '0' = 'a'): Promise<ReportLayoutItem[]> {
        return this.datatask.Post(2801, { res: res });
    }

    /**
     * Retrieves list of years which have accounting history.
     *
     * @param businessco_no the id of the company to retrieve accouting years for.
     */
    public getAccountingYears(businessco_no: string | number): Promise<AccountingYearItem[]> {
        return this.datatask.Post(2820, { businessco_no: businessco_no });
    }

    /**
     * Retrieves a list of voucher types.
     *
     * @returns a promise resolving to a list of voucher types.
     */
    public getVoucherTypes(): Promise<VoucherTypeItem[]> {
        return this.datatask.Post(1972);
    }

    /**
     * Retrieves layout data of which fields should be displayed or hidden for the given voucher type id.
     *
     * @param voucherTypeId the id of the voucher type to find layout data for.
     * @returns a promise containing an object with proprties defining which fields should be displayed or hidden.
     */
    public async getVoucherTypeLayoutData(params: GetAcVoucherTypeChanged): Promise<VoucherTypeLayoutData> {
        return (await this.datatask.Post(1975, params))[0];
    }

    /**
     * Returns the exchange-rate for the given currency and valuta combination.
     *
     * @param baseCurrencyId the currency to find the exchange rate for.
     * @param valutaId the valuta to find the exchange rate for.
     * @returns a promise containing the exchange-rate for the given currency and valuta combination.
     */
    public async getExchangeRate(baseCurrencyId: string, valutaId: string): Promise<ExchangeRate> {
        return (await this.datatask.Post(1973, { base_currency_id: baseCurrencyId, valuta_id: valutaId }))[0];
    }

    /**
     * Gets list of labourers for the given business/company id.
     *
     * @param parms parms used for retrieving list of labourers.
     * @returns a promise containing list of labourers for the given businessco.
     */
    public getLabourers(parms: { businessco_no: string | number; add_all?: '1' | '0' }): Promise<LabourerItem[]> {
        return this.datatask.Post(1531, parms);
    }

    /**
     * Retrieves list of voucher which matched the given params.
     *
     * @datatask 3748
     * @datataskName sp_ws_p2_w_accountingprocessing_voucher_no_match_list
     * @param params params used for retrieving search of voucher matches.
     * @returns promise containing a list of voucher items matching the given params.
     */
    public searchVoucherNoMatch(params: { voucher_no_match: string }): Promise<VoucherNoItem[]> {
        return this.datatask.Post(3748, params);
    }

    public searchAccounts(params: SearchAccountsParams): Promise<AccountItem[]> {
        return this.datatask.Post(3888, params);
    }

    public searchAccountingAllocations(params: SearchAccountingAllocationParams): Promise<AccountingAllocationItem[]> {
        return this.datatask.Post(3770, params);
    }
}

export interface AccountingAllocationItem {
    accountingallocation_keyno: string;
    accountingallocation_name: string;
    orderby: string;
    accountingallocation_keyname: string;
}

export interface AccountItem {
    item_id: string;
    item_name: string;
    accounttype: string;
    acvatcode: string;
    orderby: string;
}

export interface SearchAccountsParams {
    searchtext: string;
    businessco_no: string | number;
}

export interface SearchAccountingAllocationParams {
    searchtext: string;
    businessco_no: string | number;
}

/**
 * Represents a accounting year item.
 */
export interface AccountingYearItem {
    /**
     * The year of the item.
     */
    transyear: string;

    /**
     * The displayed value for the year.
     */
    transyear_name: string;

    /**
     * The order place in the list for the accounting year.
     */
    orderby: string;
}

export interface VoucherTypeItem {
    /**
     * The id of the vouchertype.
     */
    acvouchertype_id: string;

    /**
     * The displayed name of the voucher type.
     */
    acvouchertype_name: string;

    /**
     * The order of the voucher type in a list of voucher types.
     */
    orderby: string;
}

export interface VoucherNoItem {
    voucher_no: string;
    voucher_name: string;
    orderby: string;
}

/**
 * Represents layout data for voucher type.
 */
export interface VoucherTypeLayoutData {
    show_cust_name: '1' | '0';
    show_supplier_name: '1' | '0';
    show_voucherdate: '1' | '0';
    show_transdate: '1' | '0';
    show_duedate: '1' | '0';
    show_rem_invoice_no: '1' | '0';
    show_valuta_id: '1' | '0';
    show_amount_invval_abs: '1' | '0';
    show_kid: '1' | '0';
    show_bankaccount: '1' | '0';
    show_project_name: '1' | '0';
    show_department_id: '1' | '0';
    show_equipment_name: '1' | '0';
    show_labour_no: '1' | '0';
    show_businessco_no: '1' | '0';
    show_invoice_no?: '1' | '0';
    show_voucher_no_match: '1' | '0';
    show_rem_on_hold: '1' | '0';
    show_rem_never_pay: '1' | '0';
    show_bookkeep_button: '1' | '0';
}

/**
 * Represents an exchange-rate.
 */
export interface ExchangeRate {
    exchange_rate: string | number;
    exchange_factor: string | number;
    valuta_pr: string | number;
}

export interface LabourerItem {
    labour_no: string;
    labour_name: string;
    orderby: string;
}

export interface ReportLayoutItem {
    item_id: string;
    item_name: string;
    orderby: string;
    item_state: string;
    item_parms: string;
    item_path: string;
}

export interface GetAcVoucherTypeChanged {
    acvouchertype_id: string;
    accountingfunction: string;
}
